import { ThemeOptions, alpha } from "@mui/material"
import { TypographyOptions } from "@mui/material/styles/createTypography"
import { deepCopy } from "app/tools/common"
import { uiEventModule } from "ui/event"
import { UI_EVENTS } from "ui/model"
import { IUIConfiguration, IUIConfigurationFont } from "../model"
import { getCDNAbsoluteUrl } from "./common"

export const getThemeOptionsWithConfiguration = (
  isDark: boolean,
  options: ThemeOptions,
  config: IUIConfiguration
): ThemeOptions => {
  const newOptions: any = deepCopy(options)
  // To test uncomments the following lines
  // config.favicon.lightUrl = "/images/test-favicon.ico"
  // config.logos.big.lightUrl = "/images/test-logo-login.png"
  // config.logos.small.lightUrl = "/images/test-logo-menu-closed.jpg"
  // config.colors.textDisabled.light = "#ff0000"
  // FONT
  if (newOptions.typography) {
    const typography = newOptions.typography as TypographyOptions
    typography.fontFamily = config.font.googleFamily
    typography.fontWeightBold = config.font.weightBold
    typography.fontWeightLight = config.font.weightLight
    typography.fontWeightMedium = config.font.weightMedium
    typography.fontWeightRegular = config.font.weightRegular
  } else {
    console.error("Theme typography should exist")
  }
  const faviconLink: HTMLLinkElement = document.getElementById("app-favicon") as HTMLLinkElement
  if (config.page_title) {
    const pageTitle: HTMLLinkElement = document.getElementById("page-title") as HTMLLinkElement
    pageTitle.textContent = config.page_title
  }
  if (isDark) {
    // DARK FAVICON
    faviconLink.href = getCDNAbsoluteUrl(config.images.favicon.darkUrl, config)
    // DARK COMPONENTS => MuiFormControlLabel
    newOptions.components.MuiFormControlLabel.styleOverrides.root.color = config.colors.textPrimary.dark
    // DARK COMPONENTS => MuiButton
    newOptions.components.MuiButton.styleOverrides.root["&:hover"].color = config.colors.primary.dark
    newOptions.components.MuiButton.styleOverrides.root["&:hover"].backgroundColor = config.colors.textSpecial.dark
    newOptions.components.MuiButton.styleOverrides.outlined["&:hover"].color = config.colors.textSpecial.dark
    newOptions.components.MuiButton.styleOverrides.outlined["&:hover"].backgroundColor = config.colors.primary.dark
    newOptions.components.MuiButton.styleOverrides.text["&:hover"].color = config.colors.textSpecial.dark
    newOptions.components.MuiButton.styleOverrides.text["&:hover"].backgroundColor = config.colors.primary.dark
    newOptions.components.MuiButton.styleOverrides.text.color = config.colors.primary.dark
    // DARK COMPONENTS => MuiSwitch
    newOptions.components.MuiSwitch.styleOverrides.root[".MuiSwitch-colorPrimary"].color = config.colors.secondary.dark
    newOptions.components.MuiSwitch.styleOverrides.root[".MuiSwitch-colorPrimary.Mui-checked"].color =
      config.colors.textPrimary2.dark
    newOptions.components.MuiSwitch.styleOverrides.root[".MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track"] =
      config.colors.textPrimary2.dark
    // DARK COMPONENTS => MuiOutlinedInput
    newOptions.components.MuiOutlinedInput.styleOverrides.root.border = config.colors.border.dark
    // DARK COMPONENTS => MuiFormControl
    newOptions.components.MuiFormControl.styleOverrides.root[
      ".form & .MuiInputBase-root.MuiOutlinedInput-root.Mui-focused fieldset"
    ].borderColor = config.colors.form.dark
    newOptions.components.MuiFormControl.styleOverrides.root[
      ".form & .MuiFormLabel-root.MuiInputLabel-root.Mui-focused"
    ].color = config.colors.form.dark
    newOptions.components.MuiFormControl.styleOverrides.root["& .MuiFormControlLabel-label"].color =
      config.colors.textPrimary.dark
    newOptions.components.MuiFormControl.styleOverrides.root["& .Mui-disabled"].color =
      `${config.colors.secondary.dark} !important`
    newOptions.components.MuiFormControl.styleOverrides.root[
      "& .MuiInputBase-root.MuiOutlinedInput-root.Mui-disabled:hover .MuiOutlinedInput-notchedOutline"
    ].borderColor = config.colors.secondary.dark
    // DARK PALETTE => border
    newOptions.palette.error.main = config.colors.error.dark
    newOptions.palette.border = `1px solid ${config.colors.border.dark}`
    newOptions.palette.specialText = config.colors.textSpecial.dark
    newOptions.palette.form = config.colors.form.dark
    newOptions.palette.boxShadow = getBoxShadow(config, true)
    newOptions.palette.formText = config.colors.textForm.dark
    newOptions.palette.primary.main = config.colors.primary.dark
    newOptions.palette.secondary.main = config.colors.secondary.dark
    newOptions.palette.background.default = config.colors.backgroundDefault.dark
    newOptions.palette.background.paper = config.colors.backgroundPaper.dark
    newOptions.palette.action.active = config.colors.primary.dark
    newOptions.palette.action.disabled = config.colors.borderDisabled.dark
    newOptions.palette.text.primary = config.colors.textPrimary.dark
    newOptions.palette.text.secondary = config.colors.textSecondary.dark
    newOptions.palette.text.primary2 = config.colors.textPrimary2.dark
    newOptions.palette.text.disabled = config.colors.textDisabled.dark
  } else {
    // LIGHT FAVICON
    faviconLink.href = getCDNAbsoluteUrl(config.images.favicon.lightUrl, config)
    // LIGHT COMPONENTS => MuiFormControlLabel
    newOptions.components.MuiFormControlLabel.styleOverrides.root.color = config.colors.textPrimary.light
    // LIGHT COMPONENTS => MuiButton
    newOptions.components.MuiButton.styleOverrides.root["&:hover"].color = config.colors.primary.light
    newOptions.components.MuiButton.styleOverrides.root["&:hover"].backgroundColor = config.colors.textSpecial.light
    newOptions.components.MuiButton.styleOverrides.outlined["&:hover"].color = config.colors.textSpecial.light
    newOptions.components.MuiButton.styleOverrides.outlined["&:hover"].backgroundColor = config.colors.primary.light
    newOptions.components.MuiButton.styleOverrides.text["&:hover"].color = config.colors.textSpecial.light
    newOptions.components.MuiButton.styleOverrides.text["&:hover"].backgroundColor = config.colors.primary.light
    newOptions.components.MuiButton.styleOverrides.text.color = config.colors.primary.light
    // LIGHT COMPONENTS => MuiCheckbox
    newOptions.components.MuiCheckbox.styleOverrides.root.color = config.colors.textPrimary2.light
    newOptions.components.MuiCheckbox.styleOverrides.root["&.Mui-checked, &.MuiCheckbox-indeterminate"].color =
      config.colors.textPrimary2.light
    newOptions.components.MuiCheckbox.styleOverrides.root["&.Mui-disabled"].color = config.colors.borderDisabled.light
    // LIGHT COMPONENTS => MuiSwitch
    newOptions.components.MuiSwitch.styleOverrides.root[".MuiSwitch-colorPrimary.Mui-checked"].color =
      config.colors.textPrimary2.light
    newOptions.components.MuiSwitch.styleOverrides.root[".MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track"] =
      config.colors.textPrimary2.light
    // LIGHT COMPONENTS => MuiOutlinedInput
    newOptions.components.MuiOutlinedInput.styleOverrides.root.border = config.colors.border.light
    // LIGHT COMPONENTS => MuiFormControl
    newOptions.components.MuiFormControl.styleOverrides.root[
      ".form & .MuiInputBase-root.MuiOutlinedInput-root.Mui-focused fieldset"
    ].borderColor = config.colors.form.light
    newOptions.components.MuiFormControl.styleOverrides.root[
      ".form & .MuiFormLabel-root.MuiInputLabel-root.Mui-focused"
    ].color = config.colors.form.light
    newOptions.components.MuiFormControl.styleOverrides.root["& .MuiFormControlLabel-label"].color =
      config.colors.textPrimary.light
    // LIGHT COMPONENTS => MuiFilledInput
    newOptions.components.MuiFilledInput.styleOverrides.root["& input"].backgroundColor =
      config.colors.textSpecial.light
    // LIGHT PALETTE
    newOptions.palette.error.main = config.colors.error.light
    newOptions.palette.border = `1px solid ${config.colors.border.light}`
    newOptions.palette.specialText = config.colors.textSpecial.light
    newOptions.palette.form = config.colors.form.light
    newOptions.palette.boxShadow = getBoxShadow(config, false)
    newOptions.palette.formText = config.colors.textForm.light
    newOptions.palette.primary.main = config.colors.primary.light
    newOptions.palette.secondary.main = config.colors.secondary.light
    newOptions.palette.background.default = config.colors.backgroundDefault.light
    newOptions.palette.background.paper = config.colors.backgroundPaper.light
    newOptions.palette.action.disabled = config.colors.borderDisabled.light
    newOptions.palette.text.primary = config.colors.textPrimary.light
    newOptions.palette.text.secondary = config.colors.textSecondary.light
    newOptions.palette.text.primary2 = config.colors.textPrimary2.light
    newOptions.palette.text.disabled = config.colors.textDisabled.light
  }
  importFont(config)
  uiEventModule.notify(UI_EVENTS.CONFIGURATION_CHANGE, config)
  return newOptions
}

export const getBoxShadow = (config: IUIConfiguration, isDark: boolean): string =>
  isDark
    ? `0 4px 12px -4px ${alpha(config.colors.boxShadow.dark, 0.6)}`
    : `0 4px 12px -4px ${alpha(config.colors.boxShadow.light, 0.2)}`

export const importFont = (uiConfig: IUIConfiguration): void => {
  const styleTag = document.createElement("style")
  styleTag.innerHTML = `
      @import url('https://fonts.googleapis.com/css2?family=${uiConfig.font.googleFamily}:wght@${getAllWeights(
        uiConfig.font
      ).join(";")}&display=swap');`
  document.head.appendChild(styleTag)
}

export const getAllWeights = (font: IUIConfigurationFont): number[] => {
  const weights: number[] = [font.weightLight]
  if (weights.indexOf(font.weightRegular) === -1) {
    weights.push(font.weightRegular)
  }
  if (weights.indexOf(font.weightMedium) === -1) {
    weights.push(font.weightMedium)
  }
  if (weights.indexOf(font.weightBold) === -1) {
    weights.push(font.weightBold)
  }
  return weights
}
